import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Wallet, 
  Shield, 
  CheckCircle2, 
  Loader2, 
  AlertTriangle,
  RefreshCw,
  IndianRupee,
  Lock
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// Load Razorpay script dynamically
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const formatCurrency = (amount) => {
  if (!amount) return "₹0";
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

export default function EscrowPayment({ deal, onPaymentSuccess, isBuyer }) {
  const [isLoading, setIsLoading] = useState(false);
  
  const escrowStatus = deal?.escrow_status || 'not_initiated';
  const escrowAmount = deal?.escrow_amount || deal?.deal_value || 0;
  
  const getStatusBadge = () => {
    switch (escrowStatus) {
      case 'funded':
        return <Badge className="bg-green-100 text-green-700">Funded</Badge>;
      case 'released':
        return <Badge className="bg-blue-100 text-blue-700">Released</Badge>;
      case 'refunded':
        return <Badge className="bg-amber-100 text-amber-700">Refunded</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700">Not Initiated</Badge>;
    }
  };

  const handleInitiatePayment = async () => {
    setIsLoading(true);
    
    try {
      // Load Razorpay script
      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) {
        toast.error("Failed to load payment gateway. Please refresh and try again.");
        setIsLoading(false);
        return;
      }

      // Create order
      const { data: orderData } = await api.post('/escrow/create-order', {
        deal_id: deal.id,
        amount: escrowAmount
      });

      // Open Razorpay checkout
      const options = {
        key: orderData.key_id,
        amount: orderData.amount_paise,
        currency: orderData.currency,
        name: "ReBusi",
        description: `Escrow Payment for Deal`,
        order_id: orderData.order_id,
        handler: async function (response) {
          try {
            // Verify payment on backend
            const verifyResponse = await api.post('/escrow/verify-payment', {
              deal_id: deal.id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            if (verifyResponse.data.success) {
              toast.success("Escrow payment successful!");
              onPaymentSuccess && onPaymentSuccess(verifyResponse.data.deal);
            }
          } catch (error) {
            toast.error(error.response?.data?.detail || "Payment verification failed");
          }
        },
        prefill: {
          name: orderData.buyer_name,
          email: orderData.buyer_email,
          contact: orderData.buyer_mobile
        },
        notes: {
          deal_id: deal.id
        },
        theme: {
          color: "#f97316" // ReBusi orange
        },
        modal: {
          ondismiss: function() {
            setIsLoading(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        toast.error(`Payment failed: ${response.error.description}`);
        setIsLoading(false);
      });
      
      razorpay.open();
      
    } catch (error) {
      console.error("Payment initiation error:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
      setIsLoading(false);
    }
  };

  // If escrow is already funded, show status card
  if (escrowStatus === 'funded' || escrowStatus === 'released' || escrowStatus === 'refunded') {
    return (
      <Card className="border-2 border-green-200 bg-green-50">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-green-800">
              <Wallet className="w-5 h-5" />
              Escrow Status
            </CardTitle>
            {getStatusBadge()}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-green-200">
            <div>
              <p className="text-sm text-slate-600">Escrow Amount</p>
              <p className="text-2xl font-bold text-green-700">{formatCurrency(escrowAmount)}</p>
            </div>
            <CheckCircle2 className="w-10 h-10 text-green-500" />
          </div>
          
          {escrowStatus === 'funded' && (
            <div className="flex items-start gap-2 p-3 bg-green-100 rounded-lg">
              <Shield className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-800">Funds Secured</p>
                <p className="text-xs text-green-700">
                  Funds are safely held in escrow and will be released to the seller upon deal completion.
                </p>
              </div>
            </div>
          )}
          
          {escrowStatus === 'released' && (
            <div className="flex items-start gap-2 p-3 bg-blue-100 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800">Funds Released</p>
                <p className="text-xs text-blue-700">
                  Escrow funds have been released to the seller.
                  {deal.escrow_released_at && ` Released on ${new Date(deal.escrow_released_at).toLocaleDateString()}`}
                </p>
              </div>
            </div>
          )}
          
          {escrowStatus === 'refunded' && (
            <div className="flex items-start gap-2 p-3 bg-amber-100 rounded-lg">
              <RefreshCw className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Funds Refunded</p>
                <p className="text-xs text-amber-700">
                  Escrow funds have been refunded to the buyer.
                  {deal.escrow_refund_reason && ` Reason: ${deal.escrow_refund_reason}`}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Show payment initiation card for buyers at LOI stage
  if (!isBuyer) {
    return (
      <Card className="border-2 border-orange-200 bg-orange-50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-orange-800">
            <Wallet className="w-5 h-5" />
            Escrow Payment
          </CardTitle>
          <CardDescription className="text-orange-700">
            Waiting for buyer to initiate escrow payment
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-orange-200">
            <div>
              <p className="text-sm text-slate-600">Expected Escrow Amount</p>
              <p className="text-2xl font-bold text-orange-600">{formatCurrency(escrowAmount)}</p>
            </div>
            <Clock className="w-10 h-10 text-orange-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Buyer can initiate payment
  return (
    <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-orange-800">
          <Wallet className="w-5 h-5" />
          Fund Escrow
        </CardTitle>
        <CardDescription className="text-orange-700">
          Securely deposit funds to proceed with the deal
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-orange-200">
          <div>
            <p className="text-sm text-slate-600">Escrow Amount</p>
            <p className="text-3xl font-bold text-orange-600">{formatCurrency(escrowAmount)}</p>
          </div>
          <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center">
            <IndianRupee className="w-7 h-7 text-orange-500" />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-start gap-2 p-3 bg-white/70 rounded-lg">
            <Shield className="w-4 h-4 text-green-600 mt-0.5" />
            <p className="text-xs text-slate-600">
              <strong className="text-green-700">Secure Payment:</strong> Funds are held safely in escrow until deal completion
            </p>
          </div>
          <div className="flex items-start gap-2 p-3 bg-white/70 rounded-lg">
            <Lock className="w-4 h-4 text-blue-600 mt-0.5" />
            <p className="text-xs text-slate-600">
              <strong className="text-blue-700">Protected:</strong> Full refund if deal doesn't close
            </p>
          </div>
        </div>

        <Button
          onClick={handleInitiatePayment}
          disabled={isLoading || !escrowAmount}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white h-12 text-base"
          data-testid="fund-escrow-btn"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Wallet className="w-5 h-5 mr-2" />
              Pay {formatCurrency(escrowAmount)}
            </>
          )}
        </Button>

        <p className="text-xs text-center text-slate-500">
          Powered by Razorpay. 100% secure payments.
        </p>
      </CardContent>
    </Card>
  );
}

// Import this to use a missing Clock icon
function Clock(props) {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      {...props}
    >
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </svg>
  );
}
